import { useMemo, useState } from 'react';
import { Activity, Filter, Search } from 'lucide-react';
import {
  BosDurum,
  Girdi,
  Rozet,
  TarihAraligiSecici,
  VeriTablo,
  tarihIso,
  type ColumnDef,
  type TarihAraligi,
} from '@emanetx/ui';
import { type CihazOlayi } from '@emanetx/sdk';
import { useCihazOlaylariListesi } from './api.js';

const TIP_ETIKET: Record<string, string> = {
  kapi_acildi: 'Kapı açıldı',
  kapi_kapandi: 'Kapı kapandı',
  pil_dustu: 'Pil düştü',
  lora_paket: 'LoRa paket',
  ping: 'Ping',
};

function sonOtuzGun(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

export interface OlaylarTablosuOzellikleri {
  /** Kapsam: belirli bir nokta veya dolap için filtre. Boş → global. */
  noktaId?: string;
  dolapId?: string;
  /** Kompakt embed modu (kart sarmayı azaltır). */
  kompakt?: boolean;
  /** Otomatik yenileme aralığı (ms). Default 15s. 0 = kapalı. */
  yenilemeAraligi?: number;
}

export function OlaylarTablosu({
  noktaId,
  dolapId,
  kompakt = false,
  yenilemeAraligi = 15_000,
}: OlaylarTablosuOzellikleri) {
  const [sayfa, setSayfa] = useState(1);
  const [tip, setTip] = useState('');
  const [cihazUid, setCihazUid] = useState('');
  const [aralik, setAralik] = useState<TarihAraligi>(sonOtuzGun);

  const filtreBaglami = !!(noktaId || dolapId);

  const sorgu = {
    sayfa,
    boyut: 50,
    ...(noktaId && { noktaId }),
    ...(dolapId && { dolapId }),
    ...(tip && { tip }),
    ...(cihazUid && { cihazUid }),
    ...(tarihIso(aralik.baslangic) && { baslangicTen: tarihIso(aralik.baslangic) }),
    ...(tarihIso(aralik.bitis) && { baslangicAKadar: tarihIso(aralik.bitis) }),
  };

  const liste = useCihazOlaylariListesi(sorgu, yenilemeAraligi);

  const kolonlar = useMemo<ColumnDef<CihazOlayi, unknown>[]>(() => {
    const cols: ColumnDef<CihazOlayi, unknown>[] = [
      {
        accessorKey: 'olusturmaTarihi',
        header: 'Zaman',
        meta: { etiket: 'Zaman' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.olusturmaTarihi).toLocaleString('tr-TR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </span>
        ),
      },
      {
        accessorKey: 'cihazUid',
        header: 'Cihaz',
        meta: { etiket: 'Cihaz' },
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.cihazUid}</span>,
      },
    ];

    if (!dolapId) {
      cols.push({
        id: 'kapsam',
        header: noktaId ? 'Dolap' : 'Nokta / Dolap',
        meta: { etiket: noktaId ? 'Dolap' : 'Nokta / Dolap' },
        enableSorting: false,
        cell: ({ row }) => {
          const o = row.original;
          if (!o.noktaKod) return <span>—</span>;
          return (
            <span className="text-xs">
              {!noktaId && <span className="text-fume-500 font-mono">{o.noktaKod}</span>}
              {o.dolapNumara && (
                <span className="font-mono">
                  {noktaId ? '' : ' / '}#{o.dolapNumara}
                </span>
              )}
            </span>
          );
        },
      });
    }

    cols.push(
      {
        accessorKey: 'tip',
        header: 'Tip',
        meta: { etiket: 'Tip' },
        cell: ({ row }) => (
          <Rozet ton="fume">{TIP_ETIKET[row.original.tip] ?? row.original.tip}</Rozet>
        ),
      },
      {
        accessorKey: 'rssi',
        header: 'RSSI',
        meta: { etiket: 'RSSI' },
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.rssi !== null && row.original.rssi !== undefined
              ? `${row.original.rssi} dBm`
              : '—'}
          </span>
        ),
      },
      {
        id: 'data',
        header: 'Data',
        meta: { etiket: 'Data' },
        enableSorting: false,
        cell: ({ row }) => (
          <details className="text-xs">
            <summary className="text-marka-700 dark:text-marka-400 cursor-pointer">Göster</summary>
            <pre className="bg-fume-100 dark:bg-fume-800 mt-2 max-w-md overflow-x-auto rounded-md p-2 text-[11px]">
              {JSON.stringify(row.original.data, null, 2)}
            </pre>
          </details>
        ),
      },
    );

    return cols;
  }, [noktaId, dolapId]);

  return (
    <div className={kompakt ? 'space-y-3' : 'space-y-4'}>
      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        varsayilanGizliKolonlar={kompakt ? { cihazUid: false, rssi: false } : undefined}
        bosDurum={
          <BosDurum
            ikon={Activity}
            baslik="Henüz olay yok"
            aciklama={
              filtreBaglami
                ? 'Bu kapsamda bu tarih aralığında olay yok.'
                : 'Tarih aralığını genişletin veya cihazlar bağlandığında telemetri burada akacak.'
            }
          />
        }
        ustAksiyonlar={
          <>
            <TarihAraligiSecici
              deger={aralik}
              onDegisim={(a) => {
                setAralik(a);
                setSayfa(1);
              }}
              etiketYok
              className="h-10 text-xs"
            />
            {!filtreBaglami && (
              <div className="relative min-w-0 flex-1">
                <Search
                  size={14}
                  className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                />
                <Girdi
                  placeholder="Cihaz UID..."
                  value={cihazUid}
                  onChange={(e) => {
                    setCihazUid(e.target.value);
                    setSayfa(1);
                  }}
                  className="h-10 pl-9 text-xs"
                />
              </div>
            )}
            <Filter size={13} className="text-fume-400" />
            <select
              value={tip}
              onChange={(e) => {
                setTip(e.target.value);
                setSayfa(1);
              }}
              className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
            >
              <option value="">Tüm olay tipleri</option>
              <option value="kapi_acildi">Kapı açıldı</option>
              <option value="kapi_kapandi">Kapı kapandı</option>
              <option value="pil_dustu">Pil düştü</option>
              <option value="lora_paket">LoRa paket</option>
              <option value="ping">Ping</option>
            </select>
          </>
        }
      />
    </div>
  );
}
